/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AlchemyContractMetadataResponse } from '../models/AlchemyContractMetadataResponse';
import type { AlchemyGetContractsForOwnerResponse } from '../models/AlchemyGetContractsForOwnerResponse';
import type { AlchemyGetFloorPriceResponse } from '../models/AlchemyGetFloorPriceResponse';
import type { AlchemyGetNFTsForCollectionResponse } from '../models/AlchemyGetNFTsForCollectionResponse';
import type { AlchemyGetNFTsResponse } from '../models/AlchemyGetNFTsResponse';
import type { AlchemyGetOwnersForCollectionResponse } from '../models/AlchemyGetOwnersForCollectionResponse';
import type { AlchemyGetOwnersForTokenResponse } from '../models/AlchemyGetOwnersForTokenResponse';
import type { AlchemyIsHolderOfCollectionResponse } from '../models/AlchemyIsHolderOfCollectionResponse';
import type { AlchemyNFTMetadata } from '../models/AlchemyNFTMetadata';
import type { AlchemyReingestContractResponse } from '../models/AlchemyReingestContractResponse';
import type { AlchemySearchContractMetadataResponse } from '../models/AlchemySearchContractMetadataResponse';
import type { AlchemySummarizeNFTAttributesResponse } from '../models/AlchemySummarizeNFTAttributesResponse';
import type { TraitsAttributeWithPrevalence } from '../models/TraitsAttributeWithPrevalence';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AlchemyCompatibleExperimentalService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * computeRarity
     * Computes the rarity of each attribute of an NFT.
     * @returns TraitsAttributeWithPrevalence OK
     * @throws ApiError
     */
    public alchemyComputeRarity({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        tokenId = '1',
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
        /** The ID of the token. Can be in hex or decimal format. **/
        tokenId?: string,
    }): CancelablePromise<Array<TraitsAttributeWithPrevalence>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/computeRarity',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
                'tokenId': tokenId,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getContractMetadata
     * Queries NFT high-level collection/contract level information.
     * @returns AlchemyContractMetadataResponse OK
     * @throws ApiError
     */
    public alchemyGetContractMetadata({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
    }): CancelablePromise<AlchemyContractMetadataResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getContractMetadata',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getContractsForOwner
     * Gets all NFT contracts held by an owner address.
     * @returns AlchemyGetContractsForOwnerResponse OK
     * @throws ApiError
     */
    public alchemyGetContractsForOwner({
        owner,
        apiKey = 'test',
        network = 'ethereum-mainnet',
        pageKey,
    }: {
        /** Address for NFT owner (can be in ENS format!). **/
        owner: string,
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** key for pagination. If more results are available, a pageKey will be returned in the response. Pass back the pageKey as a param to fetch the next page of results. **/
        pageKey?: string | null,
    }): CancelablePromise<AlchemyGetContractsForOwnerResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getContractsForOwner',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'owner': owner,
                'pageKey': pageKey,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getFloorPrice
     * Returns the floor prices of a NFT collection by marketplace.
     * @returns AlchemyGetFloorPriceResponse OK
     * @throws ApiError
     */
    public alchemyGetFloorPrice({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
    }): CancelablePromise<AlchemyGetFloorPriceResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getFloorPrice',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getNFTMetadata
     * Gets the metadata associated with a given NFT.
     * @returns AlchemyNFTMetadata OK
     * @throws ApiError
     */
    public alchemyGetNftMetadata({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        tokenId = '1',
        tokenUriTimeoutInMs,
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
        /** The ID of the token. Can be in hex or decimal format. **/
        tokenId?: string,
        /** No set timeout by default - When metadata is requested, this parameter is the timeout (in milliseconds) for the website hosting the metadata to respond. If you want to only access the cache and not live fetch any metadata for cache misses then set this value to 0. **/
        tokenUriTimeoutInMs?: number | null,
    }): CancelablePromise<AlchemyNFTMetadata> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getNFTMetadata',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
                'tokenId': tokenId,
                'tokenUriTimeoutInMs': tokenUriTimeoutInMs,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getNFTs
     * Gets all NFTs currently owned by a given address.
     * @returns AlchemyGetNFTsResponse OK
     * @throws ApiError
     */
    public alchemyGetNfTs({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddresses,
        filters,
        owner = 'vitalik.eth',
        pageKey,
        pageSize,
        tokenUriTimeoutInMs,
        withMetadata,
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Array of contract addresses to filter the responses with. Max limit 100 contracts. **/
        contractAddresses?: Array<string>,
        /** Filters are not supported **/
        filters?: Array<string>,
        /** Address for NFTMetadata owner (can be in ENS format!). **/
        owner?: string,
        /** key for pagination. If more results are available, a pageKey will be returned in the response. Pass back the pageKey as a param to fetch the next page of results. **/
        pageKey?: string | null,
        /** Number of NFTs to be returned per page. Defaults to 100. Max is 100. **/
        pageSize?: number | null,
        /** No set timeout by default - When metadata is requested, this parameter is the timeout (in milliseconds) for the website hosting the metadata to respond. If you want to only access the cache and not live fetch any metadata for cache misses then set this value to 0. **/
        tokenUriTimeoutInMs?: number | null,
        /** if set to true, returns NFTMetadata metadata. Setting this to false will reduce payload size and may result in a faster API call. Defaults to false. **/
        withMetadata?: boolean,
    }): CancelablePromise<AlchemyGetNFTsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getNFTs',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddresses[]': contractAddresses,
                'filters[]': filters,
                'owner': owner,
                'pageKey': pageKey,
                'pageSize': pageSize,
                'tokenUriTimeoutInMs': tokenUriTimeoutInMs,
                'withMetadata': withMetadata,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getNFTsForCollection
     * Gets all NFTs for a given NFT contract.
     * @returns AlchemyGetNFTsForCollectionResponse OK
     * @throws ApiError
     */
    public alchemyGetNfTsForCollection({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        limit,
        startToken,
        tokenUriTimeoutInMs,
        withMetadata,
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
        /** Sets the total number of NFTs returned in the response. Defaults to 100. **/
        limit?: number | null,
        /** A tokenID offset used for pagination. Can be a hex string, or a decimal. Users can specify the offset themselves to start from a custom offset, or to fetch multiple token ranges in parallel. **/
        startToken?: string | null,
        /** No set timeout by default - When metadata is requested, this parameter is the timeout (in milliseconds) for the website hosting the metadata to respond. If you want to only access the cache and not live fetch any metadata for cache misses then set this value to 0. **/
        tokenUriTimeoutInMs?: number | null,
        /** if set to true, returns NFTMetadata metadata. Setting this to false will reduce payload size and may result in a faster API call. Defaults to false. **/
        withMetadata?: boolean,
    }): CancelablePromise<AlchemyGetNFTsForCollectionResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getNFTsForCollection',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
                'limit': limit,
                'startToken': startToken,
                'tokenUriTimeoutInMs': tokenUriTimeoutInMs,
                'withMetadata': withMetadata,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getOwnersForCollection
     * Gets all owners for a given NFT contract.
     * @returns AlchemyGetOwnersForCollectionResponse OK
     * @throws ApiError
     */
    public alchemyGetOwnersForCollection({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        pageKey,
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
        /** key for pagination. If more results are available, a pageKey will be returned in the response. Pass back the pageKey as a param to fetch the next page of results. **/
        pageKey?: string | null,
    }): CancelablePromise<AlchemyGetOwnersForCollectionResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getOwnersForCollection',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
                'pageKey': pageKey,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * getOwnersForToken
     * Get the owner(s) for a token.
     * @returns AlchemyGetOwnersForTokenResponse OK
     * @throws ApiError
     */
    public alchemyGetOwnersForToken({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        tokenId = '1',
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
        /** The ID of the token. Can be in hex or decimal format. **/
        tokenId?: string,
    }): CancelablePromise<AlchemyGetOwnersForTokenResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/getOwnersForToken',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
                'tokenId': tokenId,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * isHolderOfCollection
     * Checks whether a wallet holds a NFT in a given collection.
     * @returns AlchemyIsHolderOfCollectionResponse OK
     * @throws ApiError
     */
    public alchemyIsHolderOfCollection({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        wallet = 'vitalik.eth',
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
        /** Wallet address to check for collection ownership. **/
        wallet?: string,
    }): CancelablePromise<AlchemyIsHolderOfCollectionResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/isHolderOfCollection',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
                'wallet': wallet,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * reingestContract
     * Triggers metadata refresh for an entire NFT collection and refreshes stale metadata after a collection reveal/collection changes.
     * @returns AlchemyReingestContractResponse OK
     * @throws ApiError
     */
    public alchemyReingestContract({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
    }): CancelablePromise<AlchemyReingestContractResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/reingestContract',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * searchContractMetadata
     * Search for a keyword across metadata of all ERC-721 and ERC-1155 smart contracts
     * @returns AlchemySearchContractMetadataResponse OK
     * @throws ApiError
     */
    public alchemySearchContractMetadata({
        query,
        apiKey = 'test',
        network = 'ethereum-mainnet',
    }: {
        /** The search string that you want to search for in contract metadata **/
        query: string,
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
    }): CancelablePromise<AlchemySearchContractMetadataResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/searchContractMetadata',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'query': query,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

    /**
     * summarizeNFTAttributes
     * Generate a summary of attribute prevalence for an NFT collection.
     * @returns AlchemySummarizeNFTAttributesResponse OK
     * @throws ApiError
     */
    public alchemySummarizeNftAttributes({
        apiKey = 'test',
        network = 'ethereum-mainnet',
        contractAddress = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
    }: {
        /** API Key **/
        apiKey?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Contract address for the NFTMetadata collection (ERC721 and ERC1155 supported). **/
        contractAddress?: string,
    }): CancelablePromise<AlchemySummarizeNFTAttributesResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/experimental/alchemy/{network}/nft/v2/{apiKey}/summarizeNFTAttributes',
            path: {
                'apiKey': apiKey,
                'network': network,
            },
            query: {
                'contractAddress': contractAddress,
            },
            errors: {
                400: `Validation Error`,
                403: `Access not enabled`,
            },
        });
    }

}